import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useResponsiveMap } from '@/hooks/useResponsiveMap';

interface ResponsiveDebugPanelProps {
  isVisible: boolean;
  onClose: () => void;
}

export const ResponsiveDebugPanel: React.FC<ResponsiveDebugPanelProps> = ({ 
  isVisible, 
  onClose 
}) => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });
  
  const { containerHeight, zoom, center, layoutSize, layoutCenter, fontSize, borderWidth } = useResponsiveMap();

  // 监听窗口大小变化
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 获取屏幕类型和详细信息
  const getScreenInfo = () => {
    const { width, height } = dimensions;
    const aspectRatio = width / height;
    
    let type = '';
    let category = '';
    let color = '';
    
    if (width >= 2560) {
      type = '4K超大屏';
      category = '桌面';
      color = 'text-purple-600';
    } else if (width >= 1920) {
      type = '1080p大屏';
      category = '桌面';
      color = 'text-blue-600';
    } else if (width >= 1680) {
      type = '1050p中屏';
      category = '桌面';
      color = 'text-green-600';
    } else if (width >= 1366) {
      type = '768p标准';
      category = '笔记本';
      color = 'text-yellow-600';
    } else if (width >= 1024) {
      type = '平板横屏';
      category = '平板';
      color = 'text-orange-600';
    } else {
      type = '移动设备';
      category = '手机';
      color = 'text-red-600';
    }

    return { type, category, color, aspectRatio };
  };

  const screenInfo = getScreenInfo();

  // 获取响应式调整建议
  const getOptimizationTips = () => {
    const { width, height } = dimensions;
    const tips = [];

    if (width < 1024) {
      tips.push('📱 移动端：建议使用垂直布局');
    }
    if (width > 2560) {
      tips.push('🖥️ 超大屏：可以考虑显示更多详细信息');
    }
    if (height < 600) {
      tips.push('📏 屏幕较矮：建议减少垂直间距');
    }
    if (screenInfo.aspectRatio > 2.0) {
      tips.push('📐 超宽屏：地图中心点已向东调整');
    }

    return tips;
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 标题 */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">📊 响应式调试面板</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>
          </div>

          {/* 实时数据 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 屏幕信息 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700">🖥️ 屏幕信息</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">分辨率:</span>
                  <span className="font-mono text-lg font-bold">
                    {dimensions.width} × {dimensions.height}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">屏幕类型:</span>
                  <span className={`font-semibold ${screenInfo.color}`}>
                    {screenInfo.type}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">设备类别:</span>
                  <span className="text-blue-600 font-medium">
                    {screenInfo.category}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">宽高比:</span>
                  <span className="font-mono">
                    {screenInfo.aspectRatio.toFixed(2)}:1
                  </span>
                </div>
              </div>
            </div>

            {/* 地图配置 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700">🗺️ 地图配置</h3>
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">容器高度:</span>
                  <span className="font-mono font-bold text-blue-600">
                    {containerHeight}px
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">布局尺寸:</span>
                  <span className="font-mono font-bold text-green-600">
                    {layoutSize}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">布局中心:</span>
                  <span className="font-mono text-purple-600">
                    [{layoutCenter[0]}, {layoutCenter[1]}]
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">缩放级别:</span>
                  <span className="font-mono font-bold text-green-600">
                    {zoom.toFixed(1)}x
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">中心点:</span>
                  <span className="font-mono text-purple-600">
                    [{center[0]}, {center[1]}]
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">字体大小:</span>
                  <span className="font-mono text-orange-600">
                    {fontSize}px
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">边框宽度:</span>
                  <span className="font-mono text-red-600">
                    {borderWidth}px
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 优化建议 */}
          {getOptimizationTips().length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">💡 优化建议</h3>
              <div className="bg-yellow-50 rounded-lg p-4">
                <ul className="space-y-2">
                  {getOptimizationTips().map((tip, index) => (
                    <li key={index} className="text-sm text-yellow-800">
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 测试说明 */}
          <div className="mt-6 p-4 bg-green-50 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">🧪 测试方法</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• 调整浏览器窗口大小观察参数变化</li>
              <li>• 使用开发者工具模拟不同设备</li>
              <li>• 观察地图在不同分辨率下的显示效果</li>
              <li>• 检查响应式断点是否正确触发</li>
            </ul>
          </div>

          {/* 关闭按钮 */}
          <div className="mt-6 text-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              关闭调试面板
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
